import { ChangeEvent, FormEvent, useEffect, useState } from "react";

import { Email } from "@/Common/Api/Contacts/Dto/Email";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { Locale } from "@/Common/Models/Localization/Locale";
import { IconBadge, LinkButton } from "@/DesignSystem";
import { Button } from "@/DesignSystem/Molecules/Button";
import AIScribeAction from "../Api/AIScribeAction";
import { AIScribeLinkedUserModel } from "../Models/AIScribeLinkedUserModel";

import styles from "./AIScribe.module.scss";

interface AIScribeLinkPopuProps {
    email: string;
    onClose?: () => void;
    onLinked?: (linkedUser: AIScribeLinkedUserModel) => void;
    onCreateAccount: () => void;
}

function AIScribeLinkPopup(props: Readonly<AIScribeLinkPopuProps>): JSX.Element {
    const { getText } = useTranslation();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [errorReason, setErrorReason] = useState("");

    const isEmailValid = Email.validateEmail(email);
    const areInputsValid = !IsNullOrWhiteSpace(email) && !IsNullOrWhiteSpace(password) && isEmailValid;

    const emailLabelClassName = IsNullOrWhiteSpace(email) ? "invalid" : "";
    const emailClassName = `form-control ${emailLabelClassName}`;

    const passwordlabelClassName = IsNullOrWhiteSpace(password) ? "invalid" : "";
    const passwordClassName = `form-control ${passwordlabelClassName}`;

    const emailValidationRule = new ValidationHelper().getEmailValidationRule();
    const emailMask: string = emailValidationRule ? emailValidationRule.EditMaskPattern : "";
    const emailValidationErrorMessage: string = (emailValidationRule?.ValidationErrorMessageLookupId) ? cacheStore.GetLocalizedValueByLookupId(emailValidationRule.ValidationErrorMessageLookupId) : "";

    useEffect(() => setEmail(props.email), [props.email]);

    function handleEmailChanged(unmaskedValue: string, maskedvalue: string): void {
        setEmail(maskedvalue);
    }

    function handlePasswordChanged(e: ChangeEvent<HTMLInputElement>): void {
        setPassword(e.target.value);
    }

    async function handleLinkClickedAsync(): Promise<void> {
        if (!areInputsValid)
            return;

        setIsSubmitting(true);
        setErrorReason("");

        const client = new AIScribeAction();
        const linkResponse = await client.linkUserAsync(email, password);

        setIsSubmitting(false);

        if (linkResponse.LinkedUser) {
            props.onLinked?.(AIScribeLinkedUserModel.fromDto(linkResponse.LinkedUser));
        }
        else if (linkResponse.LinkErrorReason) {
            setErrorReason(linkResponse.LinkErrorReason);
        }
    }

    async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
        e.preventDefault();

        if (!areInputsValid)
            return;

        await handleLinkClickedAsync();
    }

    function renderFooter(): JSX.Element {
        return (
            <div className="flex-container flex-end">
                <ButtonGroup>
                    <Button
                        category="secondary"
                        variant="neutral"
                        label={getText("CancelLabel")}
                        onClick={props.onClose}
                        dataCy="ai-scribe-login-cancel"
                    />
                    <Button
                        category="primary"
                        variant="brand"
                        label={getText("AIScribe_Link")}
                        onClick={async () => await handleLinkClickedAsync()}
                        disabled={!areInputsValid || isSubmitting}
                        dataCy="ai-scribe-login-link"
                    />
                </ButtonGroup>
            </div>
        );
    }

    function openForgotPasswordPage(): void {
        const url = new URL(cacheStore.ClinicSettingsServices.CoeurWayForgotPasswordUrl);
        url.searchParams.set("lang", sessionStore.locale === Locale.fr ? "fr" : "en");

        window.open(url.toString(), "_blank", "noopener");
    }

    return (
        <Modal
            fixedOverlayPosition
            showOverlay
            showX
            disableOverlayClose
            roundTopCorner
            width={465}
            title={getText("AIScribe_LinkPopup_Title")}
            onClose={props.onClose}
            id="ai-scribe-link-popup"
            dataCy="ai-scribe-link-popup"
            footer={renderFooter()}
            cssClass={styles.linkPopup}
        >
            <p className={styles.description}>{getText("AIScribe_LinkPopup_EnterYourCoeurWayCredentials")}</p>
            <form onSubmit={handleSubmit} className={styles.linkForm}>
                <div className={styles.input}>
                    <label className={emailLabelClassName} htmlFor="txtEmail">{getText("EmailLabel")}</label>
                    <TextboxMaskedControl
                        isValid={isEmailValid}
                        returnMaskedValue={true}
                        id="txtEmail"
                        value={email}
                        onChangeFunction={handleEmailChanged}
                        item={email}
                        mask={emailMask}
                        validationErrorMessage={emailValidationErrorMessage}
                        isEmail={true}
                        focus={true}
                        className={emailClassName}
                        maxLength={254}
                    />
                </div>
                <div className={styles.input}>
                    <div>
                        <label className={passwordlabelClassName} htmlFor="txtPassword">{getText("PasswordLabel")}</label>
                        <div className={styles.floatRight}>
                            <LinkButton
                                dataCy="ai-scribe-forgot-password"
                                onClick={openForgotPasswordPage}
                                label={getText("AIScribe_LinkPopup_ForgotPassword")}
                                underline={false}
                            />
                        </div>
                    </div>
                    <input
                        id="txtPassword"
                        className={passwordClassName}
                        type="password"
                        onChange={handlePasswordChanged}
                        value={password}
                        autoComplete="off"
                    />
                </div>
                {errorReason && (
                    <div className={styles.errorMessage}>
                        <span className={styles.errorIcon}><IconBadge size="sm" variant="error" /></span>
                        {errorReason}
                    </div>
                )}
            </form>
            <div className={styles.message}>
                <p className={styles.line}>
                    {getText("AIScribe_CreateAccountQuestion")}
                    <span className={styles.link}>
                        <LinkButton
                            dataCy="ai-scribe-create-account"
                            label={getText("AIScribe_CreateAccount")}
                            onClick={() => props.onCreateAccount()}
                        />
                    </span>
                </p>
            </div>
        </Modal>
    );
}

export { AIScribeLinkPopup };
